import { useMemo, useState } from 'react'
import useUsersById from '@/hooks/useUsersById'
import {
  useApproveDocument,
  useGetDocumentApprovalState,
  useRejectDocument,
  useRemoveApprovalPolicy,
} from '@/service-library/hooks/document-approvals'
import { DocumentApprovalState, OnBehalfOf } from '@/types/approvals'
import { isForbiddenError, isStaleStateError } from '@/utils/approval-actions'
import { showErrorSnackbar, showWarningSnackbar } from '@/utils/snackbars'

type UseDocumentApprovalActionsOptions = {
  documentId: string
  onDecisionSaved?: (
    approvalState: DocumentApprovalState,
    isOnBehalf: boolean,
  ) => void
}

export default function useDocumentApprovalActions({
  documentId,
  onDecisionSaved,
}: UseDocumentApprovalActionsOptions) {
  // A set rather than a single id — approving one policy and rejecting
  // another overlap in flight, and each must only re-enable its own row.
  const [pendingPolicyIds, setPendingPolicyIds] = useState<Set<string>>(
    () => new Set(),
  )

  const addPendingPolicy = (policyId: string) => {
    setPendingPolicyIds((prev) => new Set(prev).add(policyId))
  }

  const removePendingPolicy = (policyId: string) => {
    setPendingPolicyIds((prev) => {
      if (!prev.has(policyId)) return prev
      const next = new Set(prev)
      next.delete(policyId)
      return next
    })
  }

  const {
    approvalState,
    isPending,
    isError,
    refetch,
    queryKey: approvalStateQueryKey,
  } = useGetDocumentApprovalState({ id: documentId })

  const handleWriteError = (
    error: unknown,
    policyId: string,
    isOnBehalf: boolean,
  ) => {
    if (isStaleStateError(error)) {
      showWarningSnackbar(
        'The document has changed while you were reviewing it. Please refresh the page and try again.',
      )
      // Keep the policy's controls disabled until the corrective refetch
      // lands — otherwise an immediate retry re-sends the same stale
      // expected_state_version_id and 409s again.
      refetch().finally(() => removePendingPolicy(policyId))
      return
    }
    removePendingPolicy(policyId)
    if (isForbiddenError(error)) {
      showErrorSnackbar(
        isOnBehalf
          ? "You can't act on behalf of this approver."
          : 'You are not an approver on this policy.',
      )
      return
    }
    showErrorSnackbar('Failed to record your decision. Please try again later.')
  }

  const { approveDocument } = useApproveDocument({
    documentId,
    approvalStateQueryKey,
    onSuccess: (data, variables) => {
      removePendingPolicy(variables.approval_policy_id)
      onDecisionSaved?.(data.approval_state, !!variables.on_behalf_of_user_id)
    },
    onError: (error, variables) =>
      handleWriteError(
        error,
        variables.approval_policy_id,
        !!variables.on_behalf_of_user_id,
      ),
  })

  const { rejectDocument } = useRejectDocument({
    documentId,
    approvalStateQueryKey,
    onSuccess: (data, variables) => {
      removePendingPolicy(variables.approval_policy_id)
      onDecisionSaved?.(data.approval_state, !!variables.on_behalf_of_user_id)
    },
    onError: (error, variables) =>
      handleWriteError(
        error,
        variables.approval_policy_id,
        !!variables.on_behalf_of_user_id,
      ),
  })

  const handleApprove = (policyId: string, onBehalfOf?: OnBehalfOf) => {
    if (!approvalState) return
    addPendingPolicy(policyId)
    approveDocument({
      expected_state_version_id: approvalState.state_version_id,
      approval_policy_id: policyId,
      ...(onBehalfOf && {
        on_behalf_of_user_id: onBehalfOf.userId,
        reason: onBehalfOf.reason,
      }),
    })
  }

  const handleReject = (
    policyId: string,
    reason: string | null,
    onBehalfOfUserId?: string,
  ) => {
    if (!approvalState) return
    addPendingPolicy(policyId)
    rejectDocument({
      expected_state_version_id: approvalState.state_version_id,
      approval_policy_id: policyId,
      reason,
      ...(onBehalfOfUserId && { on_behalf_of_user_id: onBehalfOfUserId }),
    })
  }

  const { removeApprovalPolicy, isPending: isRemovingPolicy } =
    useRemoveApprovalPolicy({
      documentId,
      approvalStateQueryKey,
      onSuccess: (_data, variables) =>
        removePendingPolicy(variables.approval_policy_id),
      onError: (error, variables) => {
        const policyId = variables.approval_policy_id
        if (isStaleStateError(error)) {
          showWarningSnackbar(
            'The document has changed while you were reviewing it. Please refresh the page and try again.',
          )
          refetch().finally(() => removePendingPolicy(policyId))
          return
        }
        removePendingPolicy(policyId)
        if (isForbiddenError(error)) {
          showErrorSnackbar(
            "You don't have permission to change this document's approval policies.",
          )
          return
        }
        showErrorSnackbar(
          'Failed to remove the approval policy. Please try again later.',
        )
      },
    })

  /**
   * Every write sends the document version it read, and the server only takes
   * the current one. Two writes started together would both send the old
   * version, so removes share `pendingPolicyIds` with approve and reject.
   *
   * Only one remove runs at a time on top of that. The remove hook saves a
   * single copy of the list to put back if the write fails, so a second remove
   * would overwrite the first one's copy — and a failure would then put back a
   * list missing a policy that is still attached on the server.
   */
  const handleRemovePolicy = (policyId: string) => {
    if (!approvalState || isRemovingPolicy) return
    addPendingPolicy(policyId)
    removeApprovalPolicy({
      expected_state_version_id: approvalState.state_version_id,
      approval_policy_id: policyId,
    })
  }

  const approverUserIds = useMemo(() => {
    const ids = new Set<string>()
    approvalState?.policies.forEach((policy) => {
      policy.approvers.forEach((approver) => {
        if (approver.kind === 'user') {
          ids.add(approver.user_id)
          const actedBy = approver.latest_action?.acted_by_user_id
          if (actedBy) ids.add(actedBy)
        }
      })
    })
    return [...ids]
  }, [approvalState])

  const { usersById, isPending: usersPending } = useUsersById({
    userIds: approverUserIds,
    enabled: approverUserIds.length > 0,
  })

  return {
    approvalState,
    isPending,
    isError,
    refetch,
    queryKey: approvalStateQueryKey,
    usersById,
    usersPending,
    handleApprove,
    handleReject,
    handleRemovePolicy,
    isRemovingPolicy,
    pendingPolicyIds,
  }
}
